import React, {useState} from 'react';
import moment from 'moment';
import z from 'zod';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import sample from 'lodash/sample';
import times from 'lodash/times';

import Box from '@mui/material/Box';
import CustomInputLabel from '../common/CustomInputLabel';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Stack from '@mui/material/Stack';

import {colorOptions} from '../common/ColorPicker';
import DateRangeFilter from '../common/DateRangeFilter';
import Form from '../common/Form';
import SessionConfigEditor from './SessionConfigEditor';

const sections = [
  {
    name: 'general',
  },
  {
    name: 'location',
    label: '📍 Anchor & Radius',
    description: 'Set the geographic service area for all in-home and mobile sessions. This address is used to calculate your travel radius and remains hidden from patients.',
  },
  {
    name: 'times', 
    label: '🗓️ Calendar Settings',
    description: 'Select the times when you are available for this zone.',
  },
  {
    name: 'serviceTypes',
    label: '🏋️ Service Types',
    description: 'Customize location types for each service type below',
  },
  {
    name: 'config',
    label: '⚙️ Customization Options',
    // description: 'Select the location where you are available',
  },
  {
    name: 'email',
    label: '📬 Email Settings',
  },
];

const getFields = ({editing, formData}) => ([
  {
    name: 'title',
    section: 'general',
    label: 'Zone Name',
    displayName: 'Name',
    type: 'text',
    placeholder: 'Enter a name...',
    defaultValue: 'Default',
    required: true,
    hideIfAvailabilityHidden: false,
    validate: str => !!str,
    appendDivider: false,
    grid: {xs: 12, md: 12},
  },
  {
    name: 'css_color',
    section: 'general',
    label: 'Zone Color',
    displayName: 'Zone Color',
    type: 'color',
    required: false,
    defaultValue: sample(colorOptions()),
    hideIfAvailabilityHidden: false,
    validate: () => true,
    appendDivider: true,
    grid: {xs: 12, md: 12},
  },
  {
    name: 'anchor_location',
    section: 'location',
    label: 'Anchor Point Address',
    displayName: 'Anchor Point',
    // helperText: 'This is the center point for calculating your travel radius.',
    type: 'address',
    editable: false,
    placeholder: 'Find Location',
    useGeocoding: true,
    showAddress: false,
    required: true,
    hideIfAvailabilityHidden: false,
    color: 'secondary',
    autoFocus: false,
    validate: () => true,
    appendDivider: false,
    grid: {xs: 12, md: 12},
  },
  {
    name: 'distance',
    section: 'location',
    label: 'Travel Radius (miles)',
    displayName: 'Distance',
    // helperText: 'How far are you willing to travel?',
    type: 'slider',
    step: 1,
    min: 0,
    max: 30,
    fullWidth: true,
    color: 'secondary',
    marks: times(7, n => ({ value: n * 5, label: `${n * 5}` })),
    placeholder: null,
    defaultValue: 10,
    required: true,
    hideIfAvailabilityHidden: false,
    maxWidth: 200,
    validate: str => typeof str === 'number' && str >= 0 && str <= 500,
    appendDivider: false,
    grid: {xs: 12, md: 12},
  },
    {
    name: 'days',
    section: 'times',
    label: 'Days Available',
    displayName: 'Days Available',
    type: 'daysOfWeek',
    defaultValue: ['MON'],
    placeholder: null,
    required: true,
    value: null,
    nameOnly: true,
    multi: true,
    color: 'purple',
    // validate: values => !!values?.length,
    grid: {xs: 12, md: 12},
    schema: z.array(z.string()).min(1).max(7),
  },
  {
    name: 'start_minute',
    section: 'times',
    label: 'Start Time',
    displayName: 'Start Time',
    type: 'time',
    defaultValue: '09:00 am', // 9am
    required: true,
    value: null,
    color: 'purple',
    // validate: (value => typeof value === 'number' && value >= 0 && value < 60 * 24,)
    // validate: value => /^([0-1]?[0-9]|2[0-3]):[0-5][0-9] (am|pm)$/i.test(value),
    grid: {xs: 3, md: 6},
  },
  {
    name: 'end_minute',
    section: 'times',
    label: 'End Time',
    displayName: 'End Time',
    type: 'time',
    defaultValue:'05:00 pm', // 5pm
    required: true,
    value: null,
    color: 'purple',
    // validate: value => typeof value === 'number' && value >= 0 && value < 60 * 24,
    grid: {xs: 3, md: 6},
  },
  {
    name: 'availability_frequency_minutes',
    section: 'times',
    label: 'Session Intervals',
    displayName: 'Session Intervals',
    helperText: 'Granularity for available start times',
    type: 'options',
    placeholder: null,
    defaultValue: '30',
    appendDivider: false,
    required: true,
    hideIfAvailabilityHidden: true,
    color: 'purple',
    // maxWidth: 250,
    fullWidth: true,
    validate: () => true,
    options: [
      { value: '15', label: 'Every 15 minutes' },
      { value: '30', label: 'Every 30 minutes' },
      { value: '60', label: 'Every 60 minutes' },
    ],
    grid: {xs: 6, md: 12},
  },
  {
    name: 'date_range',
    section: 'times',
    Component: props => (
      <Box style={{minHeight: '18px'}}>
        <Stack>
            <CustomInputLabel>
              Date Range
            </CustomInputLabel>
        {formData.date_range
          ? <Stack direction='row' spacing={1} alignItems='center'>
            {
              moment(formData.date_range.startDate).isValid()
              ? moment(formData.date_range.startDate).format('M/D/YYYY')
              : 'No start'
            }
            {' '}-{' '}
            {
              moment(formData.date_range.endDate).isValid()
              ? moment(formData.date_range.endDate).format('M/D/YYYY')
              : 'No end'
            }
            <IconButton
              type='button'
              size='small'
              color='error'
              onClick={e => props.onChange(e, null)}
            >
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Stack>
        : <DateRangeFilter
            {...props}
            onSubmit={(e, val) => props.onChange(e, val)}
          />
        }
        </Stack>
      </Box>
    ),
    label: 'Restrict Date Range',
    displayName: 'Date range',
    helperText: 'Set a range of dates when you can accept sessions.',
    required: false,
    defaultValue: {
      startDate: moment().startOf('day').toISOString(),
      endDate: null,
    },
    style: {marginTop: '1em'},
    buttonVariant: 'text',
    includeMonthOptions: true,
    appendDivider: false,
    hideIfAvailabilityHidden: false,
    // validate: () => true,
    grid: {xs: 12, md: 12},
  },
  {
    name: 'configs',
    section: 'serviceTypes',
    label: 'Session Types',
    displayName: 'Session Types',
    type: 'component',
    defaultValue: [],
    required: true,
    value: null,
    // validate: value => Array.isArray(value) && value.length > 0,
    grid: {xs: 12, md: 12},
    Component: props => (
      <SessionConfigEditor 
        isNewAvailabilityZone={!editing}
        geolocation={{
          lat: formData?.address?.lat || formData?.anchor_location?.lat,
          lon: formData?.address?.lon || formData?.anchor_location?.lon,
          distance: formData?.distance,
        }}
        sessionTypeConfigs={editing ? formData?.sessionTypeConfigs : []}
        {...props}
      />
    ),
    schema: z.array(z.object({
      session_type_id: z.number().min(1),
      location_types: z.array(z.string()).min(1, 'At least one location type is required'),
      facility_ids: z.array(z.number()).nullable().optional(),
    })).min(1, 'At least one session configuration is required'),
  },
  {
    name: 'time_buffer_after_minutes',
    section: 'config',
    label: 'Buffer time',
    displayName: 'Buffer time',
    type: 'options',
    helperText: 'Require buffer time between sessions',
    placeholder: null,
    appendDivider: false,
    required: false,
    defaultValue: '0',
    hideIfAvailabilityHidden: true,
    validate: () => true,
    options: [
      { value: '0', label: 'None' },
      { value: '5', label: '5 minutes' },
      { value: '10', label: '10 minutes' },
      { value: '15', label: '15 minutes' },
      { value: '20', label: '20 minutes' },
      { value: '25', label: '25 minutes' },
      { value: '30', label: '30 minutes' },
      { value: '45', label: '45 minutes' },
      { value: '60', label: '60 minutes' },
      { value: '90', label: '90 minutes' },
    ],
    grid: {xs: 12, md: 12},
  },
  {
    name: 'scheduling_window_hours',
    section: 'config',
    label: 'Advanced Notice',
    displayName: 'Advanced Notice',
    type: 'options',
    helperText: 'Minimum time needed before booking',
    placeholder: null,
    appendDivider: false,
    defaultValue: '24',
    required: false,
    hideIfAvailabilityHidden: true,
    validate: () => true,
    options: [
      { value: '1', label: '1 hour' },
      { value: '2', label: '2 hours' },
      { value: '4', label: '4 hours' },
      { value: '6', label: '6 hours' },
      { value: '8', label: '8 hours' },
      { value: '12', label: '12 hours' },
      { value: '24', label: '24 hours' },
      { value: '48', label: '2 days' },
      { value: '72', label: '3 days' },
      { value: '120', label: '5 days' },
      { value: '168', label: '7 days' },
    ],
    grid: {xs: 12, md: 12},
  },
  {
    name: 'session_meeting_url',
    section: 'config',
    label: 'Meeting link',
    type: 'text',
    helperText: 'Add a URL for virtual meetings',
    placeholder: 'meet.google.com/abc-defg-hij',
    appendDivider: false,
    required: false,
    hideIfAvailabilityHidden: false,
    validate: str => true,
    schema: z.string().nullable().optional(),
  },
  {
    name: 'is_public',
    section: 'config',
    helperText: 'Make availability visible to patients when booking',
    label: 'Make Public',
    displayName: 'Make Public',
    type: 'choice',
    defaultValue: true,
    required: false,
    hideIfAvailabilityHidden: false,
    options: [
      {value: true, label: '👋 Public'},
      {value: false, label: '🤫 Private'},
    ],
    validate: () => true,
    grid: {xs: 12, md: 12},
  },
  {
    name: 'carryforward_only_my_own_notes',
    section: 'config',
    label: 'Carry forward all provider notes',
    displayName: 'Carry forward all provider notes',
    type: 'boolean',
    helperText: 'Unselect to carry forward only my own session notes',
    defaultValue: true,
    required: false,
    hideIfAvailabilityHidden: false,
    validate: () => true,
    grid: {xs: 12, md: 12},
  },
  {
    name: 'send_provider_email',
    section: 'email',
    label: 'Provider Calendar Invitations',
    displayName: 'Send me calendar invitations',
    type: 'boolean',
    defaultValue: true,
    required: false,
    validate: () => true,
    grid: {xs: 12, md: 12},
  },
  {
    name: 'send_patient_email',
    section: 'email',
    label: 'Client Calendar Invitations',
    displayName: 'Send the client calendar invitations',
    type: 'boolean',
    defaultValue: true,
    required: false,
    validate: () => true,
    grid: {xs: 12, md: 12},
  },
]);

const filterField = ({formData, editing}) => field => {
  // if (field.name === 'session_meeting_url') {
  //   const hasVirtual = (formData?.configs || [])
  //     .some(c => c.location_types.includes('VIRTUAL'));
  //   return hasVirtual;
  // }
  return true;
};

const serializeFormData = formData => {
  const complexFields = [
    'carryforward_only_my_own_notes',
    'date_range',
    'anchor_location',
    'days',
    'configs',
    'end_minute',
    'start_minute',
  ];
  const integerFields = [
    'time_buffer_after_minutes',
    'scheduling_window_hours',
    'availability_frequency_minutes',
  ];
  let serialized = omit(formData, [...complexFields, ...integerFields]);
  serialized = integerFields.reduce((acc, field) => {
    acc[field] = parseInt(formData[field], 10);
    return acc;
  }, serialized);
  serialized.carryforward_only_my_own_notes = !formData.carryforward_only_my_own_notes;
  if (formData.date_range?.startDate) {
    serialized.date_range_start = formData.date_range?.startDate;
  }
  if (formData.date_range?.endDate) {
    serialized.date_range_end = formData.date_range?.endDate;
  }
  if (formData.anchor_location) {
    serialized.anchor_location = formData.anchor_location;
    if (formData.distance) serialized.anchor_location.radius = parseInt(formData.distance, 10);
  }
  if (formData?.days) {
    // calculate the start minute as an integer from the string hh:mm a
    const start_minute = moment(formData.start_minute, 'hh:mm a').diff(moment().startOf('day'), 'minutes');
    const end_minute = moment(formData.end_minute, 'hh:mm a').diff(moment().startOf('day'), 'minutes');
    serialized.day_availabilities = formData.days.map(d => ({
        day: d,
        start_minute,
        end_minute,
    }));
  }
  if (formData.session_meeting_url) {
    serialized.session_meeting_url = formData.session_meeting_url.startsWith('https://')
      ? formData.session_meeting_url
      : `https://${formData.session_meeting_url}`;
  }
  if (formData?.configs) {
    console.log('FORM_DATA.configs', formData.configs);
    serialized.configs = formData.configs.map(c => ({
      session_type_id: c.session_type_id,
      location_types: c.location_types,
      facility_ids: c?.facility_ids || [],
    }));
  }
  console.log('SERIALIZED', serialized);
  return serialized;
};

const serializeAvailabilityToFormData = availability => {
  const simple_fields = [
    'title',
    'css_color',
    'availability_frequency_minutes',
    'is_public',
    'send_provider_email',
    'send_patient_email',
    'carryforward_only_my_own_notes',
  ];
  let formData = pick(availability, simple_fields);
  formData.distance = (
    availability.anchor_location?.radius 
    || (availability.anchor_location?.radius === 0 ? .01 : '')
  );
  formData.availability_frequency_minutes = availability.availability_frequency_minutes.toString();
  formData.time_buffer_after_minutes = availability.time_buffer_after_minutes.toString();
  formData.scheduling_window_hours = availability.scheduling_window_hours.toString();
  formData.session_meeting_url = (
    availability?.session_meeting_url
    || ''
  ).replace('https://', '');
  formData.anchor_location = pick(
    availability.anchor_location,
    [
      'address_line_1', 'address_line_2', 
      'address_city', 'address_zip', 'address_state',
      'lat', 'lon',
    ]
  );
  formData.days = availability.day_availabilities.map(d => d.day);
  formData.start_minute = moment()
    .startOf('day')
    .add(availability.day_availabilities[0].start_minute, 'minutes')
    .format('hh:mm a');
  formData.end_minute = moment()
    .startOf('day')
    .add(availability.day_availabilities[0].end_minute, 'minutes')
    .format('hh:mm a');
  formData.date_range = {
    startDate: availability.date_range_start,
    endDate: availability.date_range_end,
  };
  formData.configs = availability.configs.map(c => ({
    session_type_id: c.session_type_id,
    location_types: c.location_types,
    facility_ids: (c.locations || []).map(l => l.id),
  }));
  return formData;
};

const AvailabilityForm = ({
  fields, 
  defaultValues,
  onSubmit,
  onFormChange,
  submitButtonText = 'Save Zone Settings',
}) => {
  // console.log('DEFAULT_VALUES', defaultValues);
  return (
    <Box>
      <Form
        // onFormChange={formData => onFormChange(formData)}
        fields={fields}
        sections={sections}
        serializer={serializeFormData}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        submitButtonText={submitButtonText}
        onFormChange={onFormChange}
      />
    </Box>
  );
};

const AvailabilityFormContainer = ({
  editing = false,
  availability = null,
  ...props
}) => {
  const childProps = {
    ...props,
  };
  const defaultValues = (
    editing
    ? serializeAvailabilityToFormData(availability)
    : getFields({
      formData: {}, 
      editing: !!props.editing,
    }).reduce((acc, field) => (
      field?.defaultValue !== undefined
      ? {
        ...acc, 
        [field.name]: field.defaultValue
      } : acc
    ), {})
  );
  const [formData, setFormData] = useState(defaultValues)
  return (
    <AvailabilityForm
      {...childProps}
      key={availability ? `editing-${availability.id}` : 'new-availability'}
      fields={
        getFields({formData, editing})
          .filter(filterField({formData, editing}))
      }
      defaultValues={defaultValues}
      onFormChange={(e, val) => setFormData(val)}
    />
  );
};

export default AvailabilityFormContainer;